/**
 * Shared CSV export (Samuel 2026-09-10).
 *
 * Used by the patient tables so a doctor can export their own list and an
 * admin can export the full one. The rows handed in are ALWAYS the caller's
 * already-filtered set: the export must match what the page is showing, and
 * scoping (whose patients these are) belongs to the query that fetched them,
 * never to this function.
 *
 * `csvCell` also neutralises spreadsheet formula injection - a patient note
 * beginning `=`, `+`, `-` or `@` would otherwise execute when the file is
 * opened in Excel or Sheets.
 */

export const csvCell = (value: unknown): string => {
  const raw = value == null ? '' : String(value);
  // Prefix a formula-leading cell with an apostrophe so spreadsheets treat it
  // as text. Done before quoting so the guard lands inside the quotes.
  const safe = /^[=+\-@\t\r]/.test(raw) ? `'${raw}` : raw;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

export interface CsvColumn<T> {
  header: string;
  value: (row: T) => unknown;
}

export const buildCsv = <T,>(rows: T[], columns: CsvColumn<T>[]): string => {
  const header = columns.map((column) => csvCell(column.header)).join(',');
  const body = rows.map((row) =>
    columns.map((column) => csvCell(column.value(row))).join(','),
  );
  return [header, ...body].join('\r\n');
};

/**
 * Trigger a browser download. The BOM keeps Excel from mangling non-ASCII
 * patient names, which this clinic has plenty of.
 */
export const downloadCsv = (filename: string, csv: string): void => {
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const exportRowsToCsv = <T,>(
  filename: string,
  rows: T[],
  columns: CsvColumn<T>[],
): void => downloadCsv(filename, buildCsv(rows, columns));

/** `my-patients-2026-09-10.csv` */
export const datedFilename = (prefix: string): string =>
  `${prefix}-${new Date().toISOString().slice(0, 10)}.csv`;
